import * as THREE from 'three';
import { loadEnvMap, getCachedEnvMap } from './env-map-loader';
export type EnvOverrideSpec = {
  preset?: string;
  url?: string;
  rotation?: number;
  linkCamera?: boolean;
};

const ENV_OVERRIDE_UD = 'c3dEnvOverrideKey';
const ENV_OVERRIDE_STATUS_UD = 'c3dEnvOverrideStatus';

/** Stable key for cache / feature signatures — preset or url plus rotation + camera link. */
export function envOverrideKey(eo: EnvOverrideSpec | undefined | null): string {
  if (!eo) return '';
  const src = eo.preset || eo.url || '';
  if (!src) return '';
  return `${src}|r:${eo.rotation ?? 0}|lc:${eo.linkCamera ? 1 : 0}`;
}

/**
 * Bind graph env override on a material. Returns true when envMap / rotation changed
 * synchronously (caller may set needsUpdate).
 */
export function applyGraphEnvOverride(
  mat: THREE.MeshPhysicalMaterial,
  overrides: Record<string, unknown>,
  sceneEnvironment: THREE.Texture | null,
  invalidate?: () => void,
): boolean {
  const eo = overrides._envOverride as EnvOverrideSpec | undefined;
  const sig = envOverrideKey(eo);
  const prevSig = (mat.userData as Record<string, unknown>)[ENV_OVERRIDE_UD] as string | undefined;
  const ud = mat.userData as Record<string, unknown>;

  if (!sig) {
    if (!prevSig && !mat.envMap) return false;
    mat.envMap = null;
    delete ud[ENV_OVERRIDE_UD];
    delete ud[ENV_OVERRIDE_STATUS_UD];
    return !!prevSig || !!mat.envMap;
  }

  const loadKey = eo!.preset || eo!.url!;
  const explicitUrl = !!eo!.url;
  let changed = prevSig !== sig;
  ud[ENV_OVERRIDE_UD] = sig;

  if (mat.envMapIntensity <= 0) {
    mat.envMapIntensity = 1;
    changed = true;
  }

  const cached = getCachedEnvMap(loadKey);
  if (cached) {
    ud[ENV_OVERRIDE_STATUS_UD] = 'loaded';
    if (mat.envMap !== cached) {
      mat.envMap = cached;
      changed = true;
    }
  } else {
    ud[ENV_OVERRIDE_STATUS_UD] = 'loading';
    loadEnvMap(loadKey).then((envTex) => {
      if (ud[ENV_OVERRIDE_UD] !== sig) return;
      if (envTex) {
        ud[ENV_OVERRIDE_STATUS_UD] = 'loaded';
        if (mat.envMap !== envTex) {
          mat.envMap = envTex;
          mat.needsUpdate = true;
          invalidate?.();
        }
        return;
      }
      ud[ENV_OVERRIDE_STATUS_UD] = 'failed';
      // Explicit upload URLs must not silently fall back to the scene HDR — that looks
      // identical to "override not applied". Presets may still use scene env as fallback.
      const fallback = explicitUrl ? null : (sceneEnvironment || null);
      if (mat.envMap !== fallback) {
        mat.envMap = fallback;
        mat.needsUpdate = true;
        invalidate?.();
      }
      if (explicitUrl) {
        console.warn('[applyGraphEnvOverride] Failed to load env override URL:', loadKey);
      }
    });
  }

  if ((mat as THREE.MeshPhysicalMaterial & { envMapRotation?: THREE.Euler }).envMapRotation) {
    const rotRad = ((eo!.rotation ?? 0) * Math.PI) / 180;
    const cur = (mat as THREE.MeshPhysicalMaterial & { envMapRotation: THREE.Euler }).envMapRotation;
    if (!eo!.linkCamera && Math.abs(cur.y - rotRad) > 1e-5) {
      cur.set(0, rotRad, 0);
      changed = true;
    }
  }

  return changed;
}
